import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Col, Form, Input, message, Row, Select, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { testimonyService } from "../../services/testimony.service";
import CardFooterAction from "../../components/CardFooterAction";
import ImageUploader from "../../components/ImageUpload";
import CustomTextInput from "../../components/CustomTextInput";
import CustomTextArea from "../../components/CustomTextArea";
import { useGetDetailApi } from "../../hooks/useApi";
import { useMe } from "../../hooks/useMe";
import { directorateOptions } from "../../data/dummyUser";

export default function TestimonyForm() {
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(null);
  const { Text } = Typography;

  const { data: userDataProfile } = useMe();
  const userDirectorate = userDataProfile?.data?.directorate;
  const userPosition = userDataProfile?.data?.position;

  const { data } = useGetDetailApi({
    id,
    serviceFn: testimonyService.findById,
    queryKey: ["testimony"],
  });

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        ...data,
      });
      setImageUrl(data.image);
    }
  }, [form, id, data]);

  const storeMutation = useMutation({
    mutationFn: (data) => testimonyService.store(data),
    onSuccess: () => {
      queryClient.invalidateQueries("testimony");
      message.success("Data berhasil disimpan");
      form.resetFields();
      navigate("/testimony");
    },
    onError: (error) => {
      console.error(error);
      message.error("Terjadi Kesalahan");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, data }) => testimonyService.update(uuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries("testimony");
      message.success("Data berhasil diubah");
      form.resetFields();
      navigate("/testimony");
    },
    onError: (error) => {
      console.error(error);
      message.error("Terjadi Kesalahan");
    },
  });

  const isPending = storeMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values) => {
    const imageFile = form.getFieldValue("image");
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    if (imageFile instanceof File) {
      if (!id) {
        formData.append("image", imageFile);
      } else {
        formData.append("newImage", imageFile);
      }
    }

    if (!id) {
      storeMutation.mutate(formData);
    } else {
      updateMutation.mutate({
        uuid: id,
        data: formData,
      });
    }
  };

  return (
    <>
      <Card
        title={(id ? "Ubah" : "Tambah") + " Testimoni"}
        actions={[
          <CardFooterAction
            key={"1"}
            route={"/testimony"}
            onClick={() => form.submit()}
            loading={isPending}
          />,
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          initialValues={{
            directorate:
              userPosition?.toLowerCase() === "superadmin"
                ? undefined
                : userDirectorate == "Direktorat Vokasi"
                  ? userDirectorate
                  : "Direktorat Peningkatan Kapasitas",
          }}
        >
          <Form.Item hidden name={"uuid"}>
            <Input />
          </Form.Item>

          <Col xs={24} md={12} style={{ marginBottom: 15 }}>
            <ImageUploader
              form={form}
              name="image"
              label="Gambar"
              imageFromAPI={imageUrl}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Max file size 500kb, tipe file .jpg & .png, Ukuran 330 x 300
            </Text>
          </Col>

          <Form.Item
            name={"directorate"}
            label={
              <Typography.Text>
                Direktorat
                <Typography.Text style={{ color: "#940808" }}>
                  *
                </Typography.Text>
              </Typography.Text>
            }
            required={false}
            rules={[
              {
                required: true,
                message: "Direktorat harus diisi",
              },
            ]}
          >
            <Select
              size="large"
              options={directorateOptions}
              disabled={userPosition?.toLowerCase() !== "superadmin"}
            />
          </Form.Item>

          <CustomTextInput
            label="Nama Lengkap"
            name="name"
            rules={[{ required: true, message: "Nama Lengkap harus diisi" }]}
            disabled={isPending}
          />

          <CustomTextInput
            label="Nama Lembaga Vokasi"
            name="institution"
            rules={[
              { required: true, message: "Nama Lembaga Vokasi harus diisi" },
            ]}
            disabled={isPending}
          />

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <CustomTextInput
                label="Jabatan"
                name="position"
                rules={[{ required: true, message: "Jabatan harus diisi" }]}
                disabled={isPending}
              />
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="rating"
                label={
                  <Typography.Text>
                    Rating
                    <Typography.Text style={{ color: "#940808" }}>
                      *
                    </Typography.Text>
                  </Typography.Text>
                }
                required={false}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  size="large"
                  options={[
                    { label: "1", value: 1 },
                    { label: "2", value: 2 },
                    { label: "3", value: 3 },
                    { label: "4", value: 4 },
                    { label: "5", value: 5 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <CustomTextArea
            label="Deskripsi Testimonial"
            name="description"
            rules={[
              { required: true, message: "Deskripsi harus diisi" },
              { max: 500, message: "Maksimal 500 karakter" },
            ]}
            showCharCount={true}
            disabled={isPending}
          />
        </Form>
      </Card>
    </>
  );
}
